import React from 'react';
import { Typography, Box } from '@mui/material';
import { scheduleEventColors } from './colors';
import { uploadData } from '@aws-amplify/storage';
import { buildMemoNewsML2CTS } from './newsmlBuilder';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from '@aws-amplify/auth';

/**
 * ビュー変更処理を生成
 */
export const createViewChangeHandler = (calendarState) => {
  return (view) => {
    let viewType;
    if (typeof view === 'object' && view.view && view.view.type) {
      viewType = view.view.type;
    } else if (typeof view === 'string') {
      viewType = view;
    } else {
      console.error('無効なビュータイプ:', view);
      return;
    }
    
    calendarState.setCurrentView(viewType);
    
    if (calendarState.calendarRef.current) {
      const calendarApi = calendarState.calendarRef.current.getApi();
      if (calendarApi) {
        calendarApi.changeView(viewType);
      }
    }
  };
};

/**
 * イベント表示内容をレンダリング
 */
export const createEventContentRenderer = (currentView) => {
  return (eventInfo) => {
    const area = eventInfo.event.extendedProps?.area || '本社';
    const category = eventInfo.event.extendedProps?.category || '社会';
    const person = eventInfo.event.extendedProps?.person || '';
    const title = eventInfo.event.title || '';
    const notes = eventInfo.event.extendedProps?.notes || '';
    const isPrivateCategory = category === 'プライベート';
    const isListView = currentView.startsWith('list');
    const isDayView = currentView === 'dayGridDay';
    const isMonthOrWeekView = currentView === 'dayGridMonth' || currentView === 'dayGridWeek';
    // レスポンシブ判定: スマホ(～767px) / タブレット(768～1023px) / PC(1024px～)
    const isMobile = window.innerWidth <= 767;
    const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1023;
    const isMobileOrTablet = isMobile || isTablet;

    const startDate = eventInfo.event.start ? new Date(eventInfo.event.start) : null;
    const endRawDate = eventInfo.event.end ? new Date(eventInfo.event.end) : startDate;
    const endDate = (eventInfo.event.allDay && endRawDate)
      ? new Date(endRawDate.getTime() - 24 * 60 * 60 * 1000)
      : endRawDate;

    const formatDateTimeLabel = (date) => {
      if (!date || Number.isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const mo = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}/${mo}/${d} ${h}:${min}`;
    };

    const startLabel = formatDateTimeLabel(startDate);
    const endLabel = formatDateTimeLabel(endDate);

    return (
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: isPrivateCategory ? scheduleEventColors.privateText : scheduleEventColors.text,
          border: isListView
            ? 'none'
            : `1px solid ${isPrivateCategory ? scheduleEventColors.privateBorder : scheduleEventColors.border}`,
          borderRadius: isListView ? '0' : '3px',
          '&:hover': {
            border: isListView ? 'none' : '3px solid #E1344C',
          },
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative',
          textAlign: 'left',
          height: '100%',
          minHeight: isListView ? 'auto' : '20px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: isMobileOrTablet && (isMonthOrWeekView) ? 0.5 : 1,
            mx: 0,
            width: '100%',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.2,
          }}
        >
          <Box
            sx={{
              fontSize: isMobileOrTablet && (isMonthOrWeekView)
                ? '0.7rem'
                : '0.875rem',
              fontWeight: 500,
            }}
          >
            {(isMobileOrTablet && isMonthOrWeekView)
              ? <Box sx={{ height: '0.2em', minHeight: '8px' }}>&nbsp;</Box>
              : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {/* 1行目: エリアタグ + カテゴリー */}
                  <Box>
                    {(area === '青森' && (!isMobileOrTablet || isDayView || isListView)) && (
                      <Box
                        component="span"
                        sx={{
                          color: '#007bbb',
                          fontWeight: 'bold',
                          display: 'inline-block',
                          mr: 0.5
                        }}
                      >
                        #青森#
                      </Box>
                    )}
                    {(area === '総支局' && (!isMobileOrTablet || isDayView || isListView)) && (
                      <Box
                        component="span"
                        sx={{
                          color: '#008000',
                          fontWeight: 'bold',
                          display: 'inline-block',
                          mr: 0.5
                        }}
                      >
                        #総支局#
                      </Box>
                    )}
                    【{category}】
                  </Box>

                  {/* 2行目: 記者名 */}
                  <Box>{person}</Box>

                  {/* 3行目: タイトル（条件付き省略: 日ビュー / リストビューは全文） */}
                  <Box>
                    {(title.length > 10 && !(isDayView || isListView))
                      ? `${title.substring(0, 10)}...`
                      : title}
                  </Box>

                  {/* 4行目以降: 開始/終了の日時 */}
                  <Box sx={{ mt: '0.5em' }}>開始：{startLabel}</Box>
                  <Box>終了：{endLabel}</Box>
                </Box>
              )
            }
          </Box>

          {/* リストビューでの編注表示 */}
          {isListView && notes && (
            <Box 
              sx={{ 
                mt: 1, 
                fontSize: '0.75rem', 
                color: '#666666',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              編注: {notes}
            </Box>
          )}
        </Box>
      </Typography>
    );
  };
};

/**
 * 複製処理を生成
 */
export const createDuplicateHandler = (calendarState) => {
  return () => {
    calendarState.setTempEvent({
      ...calendarState.tempEvent,
      id: null,
      title: `${calendarState.tempEvent.title} (複製)`,
      date: calendarState.tempEvent.date || calendarState.tempEvent.start
    });
  };
};

/**
 * NewsML 風のメモ(XML)を作成してダウンロードする処理を生成
 * 参照: #メモNewsML仕様 に合わせて適宜フィールド調整してください
 */

export const createPublishMemoHandler = (calendarState) => {
  return async () => {
    try {
      const ev = calendarState.tempEvent || {};
      const eventId = ev.id || ev._id || 'noid';

      // NewsML2CTS 形式へ統一
      const xml = buildMemoNewsML2CTS({
        themeName: (ev.title || '').trim(),
        themeContent: (ev.notes || '').trim(),
        memoType: 'NEC',
        memoPriority: 'ベタ',
        memoRegistrationDate: String(ev.start || '').slice(0, 10) || '',
        memoCategory: ev.category || '社会',
        reporterName: (ev.person || '').trim() || '未設定',
        eventStart: ev.start || new Date(),
        username: 'standalone',
        scheduleId: eventId,
        materialKind: '記事',
        materialSerial: 1,
      });

      // S3出力先: 記事は article/memoフォルダに出力
      const key = `article/memo/memo-newsml-${Date.now()}.xml`;
      
      console.log('アップロード開始:', {
        key,
        bucketName: 'schedule-memo-app2cts',
      });
      
      // AWS SDK S3クライアントを使用して直接アップロード
      const session = await fetchAuthSession();
      const s3Client = new S3Client({
        region: 'ap-northeast-1',
        credentials: session.credentials,
      });
      
      const command = new PutObjectCommand({
        Bucket: 'schedule-memo-app2cts',
        Key: key,
        Body: xml,
        ContentType: 'application/xml;charset=UTF-8',
      });
      
      const uploadResult = await s3Client.send(command);
      
      console.log('アップロード成功:', uploadResult);
      alert(`NewsML形式のメモXMLをS3に保存しました\nBucket: schedule-memo-app2cts\nKey: ${key}`);

    } catch (error) {
      console.error('メモ一覧XML作成エラー:', error);
      console.error('エラー詳細:', {
        message: error?.message,
        code: error?.code,
        statusCode: error?.statusCode,
        response: error?.response,
      });
      alert(`メモ一覧XML作成中にエラーが発生しました。\n詳細: ${error?.message || '不明なエラー'}`);
    }
  };
};

/**
 * イベントフィルタリング
 */
export const filterEvents = (events, selectedCategories, selectedAreas) => {
  return events.filter(
    (event) =>
      selectedCategories.includes(event.category) && 
      selectedAreas.includes(event.area)
  );
};